'use client';

import { FC, IframeHTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import { getVideoId } from '../utils';
import Script from 'next/script';
import { normalizeTrackingValue, trackEvent } from '@tuic/tracking';
import { twMerge } from '@tuic/tailwind-config';
import { useFullscreenListener } from '../useFullscreenListener';
declare global {
  interface Window {
    /**
     * Called by the YouTube API when it's ready
     */
    onYouTubeIframeAPIReady: () => void;
    YT: typeof YT;
  }
}
declare namespace YT {
  enum PlayerState {
    UNSTARTED = -1,
    ENDED = 0,
    PLAYING = 1,
    PAUSED = 2,
    BUFFERING = 3,
    CUED = 5,
  }
  enum PlayerError {
    InvalidParam = 2,
    Html5Error = 5,
    VideoNotFound = 100,
    EmbeddingNotAllowed = 101,
    EmbeddingNotAllowed2 = 150,
  }

  /**
   * Creates and controls a YouTube player in an <iframe>
   *
   * CAUTION: `getVideoData` is an undocumented and unofficial part
   * of the YT.Player interface and may stop working at any time.
   */
  class Player {
    constructor(elt: HTMLElement, options?: PlayerOptions);
    constructor(id: string, options?: PlayerOptions);
    playVideo(): void;
    pauseVideo(): void;
    stopVideo(): void;
    getPlayerState(): PlayerState;
    getVideoData: () => {
      title: string;
      video_id: string;
    };
    getVideoUrl(): string;
    getVideoEmbedCode(): string;
    destroy(): void;
  }
  interface PlayerOptions {
    videoId?: string;
    events?: Events;
    host?: string;
  }
  interface Events {
    onReady?: PlayerEventHandler<PlayerEvent>;
    onStateChange?: PlayerEventHandler<OnStateChangeEvent>;
    onError?: PlayerEventHandler<OnErrorEvent>;
  }
  interface PlayerEvent {
    target: Player;
  }
  interface OnStateChangeEvent extends PlayerEvent {
    data: PlayerState;
  }
  interface OnErrorEvent extends PlayerEvent {
    data: PlayerError;
  }
  interface PlayerEventHandler<TEvent extends PlayerEvent> {
    (event: TEvent): void;
  }
}
export type YouTubeVideoProps = {
  iframeProps: IframeHTMLAttributes<HTMLIFrameElement> | undefined;
};

/**
 * A component for embedding a YouTube video via the YouTube IFrame API
 * @see https://developers.google.com/youtube/iframe_api_reference
 * @see https://developers.google.com/youtube/player_parameters
 *
 * @param iframeProps - Props to pass to the underlying iframe element
 */
export const YouTubeVideo: FC<YouTubeVideoProps> = ({
  iframeProps
}) => {
  let iframePlayerRef = useRef<HTMLIFrameElement>(null);
  let hasPlayer = useRef<boolean>(false);
  let [videoTitle, setVideoTitle] = useState<string>('');
  let [videoID, setVideoID] = useState<string>('');
  useFullscreenListener(iframePlayerRef, videoTitle, videoID);
  const onPlaybackChange = useCallback((event: YT.OnStateChangeEvent) => {
    const videoData = event.target.getVideoData?.();
    const title = normalizeTrackingValue(videoData?.title);
    switch (event.data) {
      case YT.PlayerState.ENDED:
        trackEvent({
          event_name: 'video-completed',
          video_name: title,
          video_id: videoData?.video_id
        });
        break;
      case YT.PlayerState.PAUSED:
        trackEvent({
          event_name: 'video-pause',
          video_name: title,
          video_id: videoData?.video_id
        });
        break;
      case YT.PlayerState.PLAYING:
        trackEvent({
          event_name: 'video-play',
          video_name: title,
          video_id: videoData?.video_id
        });
        break;
    }
  }, []);
  const onPlayerReady = useCallback((event: YT.PlayerEvent) => {
    const videoData = event.target.getVideoData?.();
    setVideoTitle(normalizeTrackingValue(videoData?.title));
    setVideoID(videoData?.video_id);

    // Playback is triggered to ensure the first 'video-play' event is reliably tracked
    // The autoplay setting in the YouTube URL doesn't reliably trigger onPlaybackChange
    event.target.playVideo();
  }, []);
  const createNewYTPlayer = useCallback(() => {
    if (hasPlayer.current || !iframePlayerRef.current || !iframeProps?.src) return;
    hasPlayer.current = true;
    new YT.Player(iframePlayerRef.current, {
      videoId: getVideoId('youtube', iframeProps.src),
      events: {
        onReady: onPlayerReady,
        onStateChange: onPlaybackChange
      }
    });
  }, [iframeProps?.src, onPlayerReady, onPlaybackChange]);
  useEffect(() => {
    if (window.YT && typeof window.YT.Player === 'function') {
      createNewYTPlayer();
    } else {
      window.onYouTubeIframeAPIReady = () => createNewYTPlayer();
    }
  }, [createNewYTPlayer]);
  return <>
      <iframe ref={iframePlayerRef} allowFullScreen allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" width="100%" height="100%" className={twMerge(iframeProps?.className, 'h-full w-full bg-transparent')} {...iframeProps} />
      <Script src="https://www.youtube.com/iframe_api" data-sentry-element="Script" data-sentry-source-file="YouTubeVideo.tsx" />
    </>;
};